import {
  computed,
  onBeforeUnmount,
  ref,
  watch,
  type Ref,
} from 'vue'

function isOverHorizontalScrollbar(el: HTMLElement, clientX: number, clientY: number) {
  const rect = el.getBoundingClientRect()
  if (
    clientX < rect.left
    || clientX > rect.right
    || clientY < rect.top
    || clientY > rect.bottom
  ) {
    return false
  }
  if (el.scrollWidth <= el.clientWidth) {
    return false
  }
  const scrollbarHeight = el.offsetHeight - el.clientHeight
  // 独立横向滚动条区域（无垂直滚动槽）：整块视为横向滚动热区
  if (scrollbarHeight <= 0) {
    return true
  }
  return clientY >= rect.bottom - scrollbarHeight
}

function isHorizontalWheelIntent(event: WheelEvent, altModifier: boolean) {
  return event.altKey || altModifier || event.shiftKey
}

function resolveHorizontalWheelDelta(event: WheelEvent) {
  if (event.shiftKey) {
    return event.deltaX || event.deltaY
  }
  return event.deltaY || event.deltaX
}

export interface UseHorizontalScrollOptions {
  /** Shift/Alt + 滚轮监听区域（通常为 viewport，覆盖固定列） */
  wheelRef: Ref<HTMLElement | null>
  /** 实际写入 scrollLeft / scrollTop 的滚动容器 */
  scrollRef: Ref<HTMLElement | null>
  /** 横向滚动条提示检测区域；缺省时用 hintFallbackRef */
  hintRef?: Ref<HTMLElement | null>
  hintFallbackRef?: Ref<HTMLElement | null>
  /** 是否允许横向滚动 */
  canScroll?: Ref<boolean>
  /** 是否将垂直滚轮转发到 scrollRef（固定列等非滚动层悬停时） */
  canScrollVertically?: Ref<boolean>
  /** 限制 scrollLeft 上限，避免滚过内容区 */
  resolveMaxScrollLeft?: (el: HTMLElement) => number
}

function shouldForwardVerticalWheel(event: WheelEvent, scrollEl: HTMLElement) {
  const target = event.target
  if (target instanceof Node && scrollEl.contains(target)) {
    return false
  }
  return true
}

function hasVerticalScrollRange(el: HTMLElement) {
  return el.scrollHeight - el.clientHeight > 0
}

function canConsumeVerticalWheel(el: HTMLElement, deltaY: number) {
  if (!deltaY || !hasVerticalScrollRange(el)) {
    return false
  }
  const maxScrollTop = el.scrollHeight - el.clientHeight
  if (deltaY < 0 && el.scrollTop <= 0) {
    return false
  }
  if (deltaY > 0 && el.scrollTop >= maxScrollTop - 1) {
    return false
  }
  return true
}

export function useHorizontalScroll(options: UseHorizontalScrollOptions) {
  const showScrollHint = ref(false)
  const hintTargetRef = computed(
    () => options.hintRef?.value ?? options.hintFallbackRef?.value ?? null
  )

  let altModifier = false

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Alt') {
      altModifier = true
    }
  }

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Alt') {
      altModifier = false
    }
  }

  const onWindowBlur = () => {
    altModifier = false
  }

  const canScrollHorizontally = () => {
    if (options.canScroll) {
      return options.canScroll.value
    }
    const el = options.scrollRef.value
    if (!el) {
      return false
    }
    return el.scrollWidth > el.clientWidth
  }

  const onHintMouseMove = (event: MouseEvent) => {
    const el = hintTargetRef.value
    if (!el) {
      showScrollHint.value = false
      return
    }
    showScrollHint.value = isOverHorizontalScrollbar(el, event.clientX, event.clientY)
  }

  const onHintMouseLeave = () => {
    showScrollHint.value = false
  }

  const onWheel = (event: WheelEvent) => {
    const el = options.scrollRef.value
    const horizontalIntent = isHorizontalWheelIntent(event, altModifier)

    if (horizontalIntent) {
      if (!el || !canScrollHorizontally()) {
        return
      }
      const delta = resolveHorizontalWheelDelta(event)
      if (!delta) {
        return
      }
      event.preventDefault()
      const maxLeft = options.resolveMaxScrollLeft?.(el)
        ?? Math.max(0, el.scrollWidth - el.clientWidth)
      el.scrollLeft = Math.min(Math.max(0, el.scrollLeft + delta), maxLeft)
      return
    }

    if (!el || !options.canScrollVertically?.value) {
      return
    }

    const deltaY = event.deltaY
    if (!deltaY) {
      return
    }

    if (!hasVerticalScrollRange(el)) {
      return
    }

    if (canConsumeVerticalWheel(el, deltaY)) {
      if (shouldForwardVerticalWheel(event, el)) {
        event.preventDefault()
        el.scrollTop += deltaY
      }
      return
    }

    // 已到顶/底：阻止滚动链传到外层页面
    event.preventDefault()
  }

  let hintBoundEl: HTMLElement | null = null
  let wheelBoundEl: HTMLElement | null = null
  let modifierBound = false

  const bindModifiers = (active: boolean) => {
    if (modifierBound === active) {
      return
    }
    modifierBound = active
    if (active) {
      window.addEventListener('keydown', onKeyDown)
      window.addEventListener('keyup', onKeyUp)
      window.addEventListener('blur', onWindowBlur)
    } else {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onWindowBlur)
      altModifier = false
    }
  }

  const bindHint = (el: HTMLElement | null) => {
    if (hintBoundEl === el) {
      return
    }
    if (hintBoundEl) {
      hintBoundEl.removeEventListener('mousemove', onHintMouseMove)
      hintBoundEl.removeEventListener('mouseleave', onHintMouseLeave)
    }
    hintBoundEl = el
    showScrollHint.value = false
    if (el) {
      el.addEventListener('mousemove', onHintMouseMove)
      el.addEventListener('mouseleave', onHintMouseLeave)
    }
  }

  const bindWheel = (el: HTMLElement | null) => {
    if (wheelBoundEl === el) {
      return
    }
    if (wheelBoundEl) {
      wheelBoundEl.removeEventListener('wheel', onWheel)
    }
    wheelBoundEl = el
    bindModifiers(!!el)
    if (el) {
      el.addEventListener('wheel', onWheel, { passive: false })
    }
  }

  watch(hintTargetRef, bindHint, { immediate: true })
  watch(() => options.wheelRef.value, bindWheel, { immediate: true })

  onBeforeUnmount(() => {
    bindHint(null)
    bindWheel(null)
  })

  return {
    showScrollHint,
  }
}
